// =============================================================================
// features/conversations/inboxColor.ts — marcador determinístico por inbox
// (F47-S07/S09 + 2 rodadas de revisão de segurança/design do PR #667).
//
// Marcador discreto (ponto ou anel) que ajuda a bater o olho e separar
// caixas quando a operação tem 2 conversas do mesmo contato em canais
// diferentes. A cor NUNCA é o único diferenciador — o nome real da inbox
// (`channelName`) sempre acompanha (doc 18 §12, acessibilidade).
//
// Fronteira de responsabilidade (F47-S09):
//   - O SERVIDOR decide IDENTIDADE — `conversation.channelColorIndex` já vem
//     pronto no DTO, calculado sobre TODOS os canais ATIVOS da organização
//     (`created_at ASC, id ASC`), nunca sobre o subconjunto que este ator
//     enxerga (era o bug do F47-S07: `GET /api/channels` exige
//     `channel.connect`, permissão que em produção só admin/gestor_geral
//     têm — o resto da operação via 403 e o nome/cor sumiam).
//   - O FRONT decide APARÊNCIA — mapeia esse índice para um MARCADOR
//     (cor + forma) do design system. NUNCA recalcula ranking/ordenação
//     aqui.
//
// Por que cor sozinha não bastava (achado real de produção, PR #667):
//   O doc 18 só tem 3 matizes de marca (§3.1). Um 4º tom precisava vir de
//   um neutro (§3.2) ou de uma variante `-light`/`-deep` do mesmo matiz —
//   e os dois casos reais medidos em produção colidiam visualmente como
//   bolinha de 6px: `--brand-azul` (#1B3A8C, canal oficial, maior volume)
//   e `--text` (#14213D, caixa de uma vendedora) são dois azuis-marinho
//   quase idênticos.
//
// Solução (forma): as 3 cores de marca com DUAS FORMAS — ponto PREENCHIDO
// (índices 0-2) e ponto VAZADO/anel (índices 3-5, mesma cor do índice
// correspondente -3, borda em vez de fundo). Diferença por forma sobrevive
// a qualquer colisão de matiz, continua 100% dentro dos tokens canônicos
// do doc 18 (nenhum hex solto) e dobra a capacidade antes de repetir (6
// marcadores em vez de 4) — importa porque cada vendedor novo é uma caixa
// nova.
//
// Solução (contraste, achado do 2º security review): `--brand-amarelo`
// (#F5C518) contra os fundos claros do tema light mede ~1,5:1 — bem abaixo
// do mínimo de 3:1 da WCAG 1.4.11 para componente não-textual. Não é caso
// hipotético: o índice real de produção que carrega essa cor já está em
// tela. `--brand-amarelo-deep` (#D4A510) sozinho também não resolve
// (~2,1:1). Amarelo é intrinsecamente claro — nenhum tom de amarelo do
// doc 18 alcança 3:1 contra `--bg`/`--bg-elev-*` no tema claro.
//
// A correção NÃO troca a cor: adiciona um contorno neutro de 1px em
// `var(--text)` — o neutro de maior contraste do design system (doc 18
// §3.2) — em TODO marcador, independente da cor de marca escolhida. Como
// esse contorno por si só mede >12:1 contra qualquer superfície de fundo
// usada pelo produto (`--bg`, `--bg-elev-1`, `--bg-elev-2`) nos dois
// temas, a legibilidade do marcador passa a depender do contorno, nunca
// do matiz — resolve as 3 cores de uma vez, não só o amarelo (ver
// getInboxMarkerStyle abaixo para os números medidos).
//
// O QUE ESTE ARQUIVO NÃO GARANTE (para não repetir o erro de comentário
// da rodada anterior — um comentário errado sobre acessibilidade é pior
// que nenhum comentário): as cores de marca do doc 18 §3.1 NÃO são
// uniformemente "aclaradas no escuro". Azul e verde têm variante dark mais
// clara; `--brand-amarelo` usa o MESMO hex (#F5C518) em light e dark — o
// contraste do amarelo no tema escuro é alto por acidente de fundo muito
// escuro, não por ajuste deliberado do token. A garantia de contraste
// deste módulo vem inteiramente do contorno em `--text`, nunca de uma
// suposta propriedade das cores de marca em si.
// =============================================================================

/** Os 3 únicos matizes de marca do doc 18 §3.1 — nunca hex solto. */
const INBOX_MARKER_HUES = [
  'var(--brand-azul)',
  'var(--brand-verde)',
  'var(--brand-amarelo)',
] as const;

/** Marcador visual: cor (token) + forma (preenchido ou anel). */
export interface InboxMarker {
  readonly color: string;
  /** `true` = ponto preenchido (índices 0-2). `false` = anel vazado (índices 3-5). */
  readonly filled: boolean;
}

/**
 * Paleta de 6 marcadores — 3 matizes × 2 formas. Índice `i` e índice
 * `i + 3` compartilham a MESMA cor, mas nunca a mesma forma: é isso que
 * garante distinção mesmo quando dois canais "colidiriam" de matiz.
 */
const INBOX_MARKER_PALETTE: readonly InboxMarker[] = [
  { color: INBOX_MARKER_HUES[0], filled: true },
  { color: INBOX_MARKER_HUES[1], filled: true },
  { color: INBOX_MARKER_HUES[2], filled: true },
  { color: INBOX_MARKER_HUES[0], filled: false },
  { color: INBOX_MARKER_HUES[1], filled: false },
  { color: INBOX_MARKER_HUES[2], filled: false },
];

/** Fallback estável — primeiro marcador da paleta (azul preenchido). Nunca `undefined`. */
export const DEFAULT_INBOX_MARKER: InboxMarker = INBOX_MARKER_PALETTE[0]!;

/**
 * Mapeia `channelColorIndex` (inteiro ≥ 0, vindo do DTO da conversa — ver
 * `ConversationSchema.channelColorIndex` em `apps/api/.../conversations/schemas.ts`)
 * para o marcador (cor + forma) do design system.
 *
 * Índices negativos ou não-finitos (defesa contra payload malformado) caem
 * no fallback estável. O ciclo (`% length`, agora sobre 6 marcadores) é
 * aplicado sempre — inclusive para o fallback residual `0` que o servidor
 * manda quando o canal original da conversa foi desativado (comportamento
 * aceito, doc do F47-S09: não é responsabilidade do front "consertar").
 */
export function getInboxMarkerByIndex(channelColorIndex: number): InboxMarker {
  if (!Number.isFinite(channelColorIndex) || channelColorIndex < 0) {
    return DEFAULT_INBOX_MARKER;
  }
  const index = Math.trunc(channelColorIndex) % INBOX_MARKER_PALETTE.length;
  return INBOX_MARKER_PALETTE[index] ?? DEFAULT_INBOX_MARKER;
}

/**
 * Estilo inline pronto para o marcador — única fonte de verdade de
 * tamanho/traço, usada pelo item da lista e pelo cabeçalho da conversa
 * (evita divergência entre os dois lugares que renderizam o mesmo
 * marcador).
 *
 * Ambas as formas levam um CONTORNO NEUTRO de 1px em `var(--text)` — é ele,
 * não o matiz de marca, quem garante ≥3:1 (WCAG 1.4.11) contra qualquer
 * fundo do produto. Contraste medido de `--text` (doc 18 §3.2) contra os
 * fundos onde o marcador aparece:
 *   Light: vs --bg 14.54:1 · vs --bg-elev-1 15.97:1 · vs --bg-elev-2 15.06:1
 *   Dark:  vs --bg 16.00:1 · vs --bg-elev-1 14.34:1 · vs --bg-elev-2 12.61:1
 * (calculado via luminância relativa WCAG — ver PR #667 para os números
 * das 3 cores de marca isoladas, que é o que motivou este contorno: sem
 * ele, `--brand-amarelo` mede ~1,5:1 contra os fundos claros do tema
 * light — bem abaixo do mínimo).
 *
 * Preenchido (`filled: true`): bolinha de 6px + contorno via `box-shadow`
 * (não afeta o tamanho da caixa).
 * Vazado (`filled: false`): anel de 8px, borda de 2px na cor de marca
 * (identidade visual do canal) + o MESMO contorno neutro por fora, via
 * `box-shadow` — um segundo traço concêntrico que garante contraste
 * independente da cor de marca escolhida para o anel.
 */
export interface InboxMarkerStyle {
  readonly width: number;
  readonly height: number;
  readonly borderRadius: string;
  readonly background: string;
  readonly border: string;
  readonly boxShadow: string;
}

/** Contorno neutro que garante ≥3:1 contra qualquer fundo, nos dois temas — ver números acima. */
const CONTRAST_OUTLINE = '0 0 0 1px var(--text)';

export function getInboxMarkerStyle(marker: InboxMarker): InboxMarkerStyle {
  if (marker.filled) {
    return {
      width: 6,
      height: 6,
      borderRadius: '50%',
      background: marker.color,
      border: 'none',
      boxShadow: CONTRAST_OUTLINE,
    };
  }
  return {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'transparent',
    border: `2px solid ${marker.color}`,
    boxShadow: CONTRAST_OUTLINE,
  };
}
